import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormGroup, Label, Input, Button, Table, Col, Row, Card, CardBody, CardHeader } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { invoiceActions } from '../../sagas/invoiceSlice';
import Swal from 'sweetalert2';
import { formatPrice } from '../../helpers/utility';
import { invoiceSupplierActions } from '../../sagas/invoiceSupplierSlice';
import { companyActions } from '../../sagas/companySlice';

const InvoiceForm = ({ initialValues, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [invoice, setInvoice] = useState(initialValues);
    const { invoiceNumber, isLoading } = useSelector(state => state.invoices);
    const { companies } = useSelector(state => state.companies);
    const defaultCompanyId = companies?.find((item) => item.isDefault)?.id ?? null;

    useEffect(() => {
        dispatch(companyActions.getAll({}));
        dispatch(invoiceActions.fetchNewInvoiceNumber());
    }, [dispatch]);

    useEffect(() => {
        if (invoiceNumber) {
            setInvoice(prevInvoice => ({ ...prevInvoice, numero: invoiceNumber }));
        }
    }, [invoiceNumber]);

    useEffect(() => {
        if (defaultCompanyId && !invoice?.companyId) {
            setInvoice((prev) => ({ ...prev, companyId: defaultCompanyId }));
        }
    }, [defaultCompanyId, invoice?.companyId]);

    const handleInputChange = (index, event) => {
        const { name, value } = event.target;
        const newLines = [...invoice.lines];
        newLines[index][name] = value;
        setInvoice({ ...invoice, lines: newLines });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        let payload = {
            invoice: {
                ...invoice,
                receiptMappings: invoice.receiptMappings.map(line => ({
                    receiptNoteId: line.receiptNoteId, 
                    invoicedAmount: line.invoicedAmount,     
                })),
                type: 'FACTURE',
            },

            onSuccess: (invoice) => {
                Swal.fire({
                    icon: 'success',
                    title: t('facture.invoiceSaved'),
                    text: t('facture.invoiceSavedMessage', { invoiceNumber: invoice.numero }),
                });
                onClose();
            },

            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('facture.invoiceSaveError'),
                    text: t('facture.invoiceSaveErrorMessage', { error }),
                });
                
            },
        };
        
        if (invoice.id) {
            dispatch(invoiceSupplierActions.updateInvoice(payload));
        } else {
            dispatch(invoiceSupplierActions.createInvoice(payload));
        }

    };

    return (
        <Form onSubmit={handleSubmit}>
            <Card>
                <CardBody>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('company.company', 'Entreprise')}</Label>
                                <Input
                                    type="select"
                                    value={invoice.companyId || ''}
                                    onChange={(e) => setInvoice({ ...invoice, companyId: e.target.value ? Number(e.target.value) : null })}
                                >
                                    <option value="">{t('company.selectCompany', 'Sélectionner une entreprise')}</option>
                                    {companies?.map((company) => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('facture.invoiceNumber')}</Label>
                                <Input type="text" value={invoice.numero || ''} disabled />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('facture.supplier')}</Label>
                                <Input type="text" value={invoice.supplierBusinessName} disabled />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Card className="mb-3">
                        <CardHeader>
                            <h5 className="mb-0">{t('reception.receiptNotes')}</h5>
                        </CardHeader>
                        <CardBody>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t('reception.receiptNumber')}</th>
                                        <th>{t('reception.receiptDate')}</th>
                                        <th>{t('facture.totalAmountTTC')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.receiptMappings && invoice.receiptMappings.map((receipt, index) => (
                                        <tr key={index}>
                                            <td>{receipt.receiptNoteNumber}</td>
                                            <td>{new Date(receipt.receiptDate).toLocaleDateString()}</td>
                                            <td>{formatPrice(receipt.invoicedAmount, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>

                    <Card className="mb-3">
                        <CardHeader>
                            <h5 className="mb-0">{t('facture.invoiceLines')}</h5>
                        </CardHeader>
                        <CardBody>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t('facture.productDescription')}</th>
                                        <th>{t('facture.quantity')}</th>
                                        <th>{t('facture.unitPrice')}</th>
                                        <th>{t('facture.totalHt')}</th>
                                        <th>{t('facture.tva')}</th>
                                        <th>{t('facture.totalTtc')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.lines && invoice.lines.map((line, index) => (
                                        <tr key={index}>
                                            <td>{line.description}</td>
                                            <td>{line.quantity}</td>
                                            <td>{line.unitPrice}</td>
                                            <td>{formatPrice(line.totalAmountHt, false)}</td>
                                            <td>{line.tva * 100} %</td>
                                            <td>{formatPrice(line.totalAmountTtc, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label>{t('facture.paymentMethod')}</Label>
                                <select
                                    id="paymentMethod"
                                    className="form-control"
                                    value={invoice.paymentMethod || 'ESPECES'}
                                    onChange={(e) => setInvoice({ ...invoice, paymentMethod: e.target.value })}
                                >
                                    <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                    <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                    <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                    <option value="CARD">{t('facture.modeReglement.card')}</option>
                                    <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                </select>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label>{t('facture.comment')}</Label>
                                <Input type="textarea" name="comment" value={invoice.comment} onChange={(e) => setInvoice({ ...invoice, comment: e.target.value })} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mt-3 justify-content-end">
                        <Col md={4}>
                            <div className="totals text-center">
                                <p><strong>{t('facture.totalHt')}:</strong> {formatPrice(invoice.totalAmountHt)}</p>
                                <p><strong>{t('facture.tva')}:</strong> {formatPrice(invoice.totalAmountTva)}</p>
                                <p><strong>{t('facture.totalTtc')}:</strong> {formatPrice(invoice.totalAmountTtc)}</p>

                            </div>
                        </Col>
                    </Row>


                    <Button color="primary" type="submit" className="me-2">{t('common.save')}</Button>
                    <Button color="secondary" onClick={onClose}>{t('common.cancel')}</Button>
                </CardBody>
            </Card>
        </Form>
    );
};

export default InvoiceForm;
